/**
 * BatchQuestionnaireOrchestrator
 *
 * Orchestrates multiple QuestionRenderer instances for batch questionnaire completion.
 * Displays questions one-at-a-time in a wizard/stepper interface with slide animations.
 * Container-agnostic - works in chat messages, panels, or any container.
 *
 * Key Features:
 * - Wizard UI: One question at a time with progress bars and navigation
 * - Reuses existing QuestionRenderer for all question types
 * - Evaluates display rules on frontend (UX layer)
 * - Saves drafts to localStorage on every interaction (48hr expiration)
 * - Tracks progress for visible questions only
 * - Validates current question before allowing navigation
 * - Slide animations for smooth transitions
 * - Works in chat message flow or any container context
 */

import { QuestionRenderer } from './QuestionRenderer.js';

// Question types that support auto-advance (simple single-selection types only)
const AUTO_ADVANCE_TYPES = new Set([
    'SINGLE_CHOICE',
    'RATING_SCALE',
    'YES_NO'
]);

// Default pill label shown on instruction/explanation cards (overridable per-card
// via configuration.pillLabel). Instruction cards are excluded from question numbering.
const DEFAULT_INSTRUCTION_PILL_LABEL = 'Please Note';

export class BatchQuestionnaireOrchestrator {
    constructor(container, questionnaireData, options = {}) {
        this.container = container;
        this.questionnaire = questionnaireData.questionnaire;
        this.questions = questionnaireData.questions;
        this.displayRules = questionnaireData.displayRules || [];

        // State
        this.questionRenderers = new Map(); // questionId -> QuestionRenderer instance
        this.submitted = false;

        // Wizard-specific state
        this.visibleQuestions = []; // Filtered by display rules
        this.isAnimating = false; // Prevent navigation during animation
        this.showingConfirmation = false; // Track if confirmation panel is displayed

        // Group-based navigation state
        this.questionGroups = [];    // Array of arrays: [[q1, q2], [q3], [q4, q5]]
        this.currentGroupIndex = 0;  // Current group being displayed

        // Options
        this.onSubmit = options.onSubmit || null;
        this.onComplete = options.onComplete || null; // Called after successful submission
        // When true, the question-number pill is not appended to the wizard body — the
        // host mounts `questionNumberEl` itself (the side panel puts it in the header
        // title slot). All pill text/visibility updates stay owned by this class.
        this.pillInHeader = options.pillInHeader === true;
        this.questionNumberEl = null;
        this.pathMetadata = options.pathMetadata || null; // Optional path context
        this.eventBus = options.eventBus || null; // Event bus for progress updates
        this.previewMode = options.previewMode || false; // Dev-only: disables persistence/validation
        this.onRefresh = options.onRefresh || null; // Dev-only: callback returning fresh questionnaireData
        this.onNavigate = options.onNavigate || null; // Dev-only: callback(groupIndex, questions) on navigation
        this.onPreviewSubmit = options.onPreviewSubmit || null; // Dev-only: replaces onSubmit in previewMode; receives flattened responses array

        // Load draft from localStorage (skip in preview mode)
        this.draftResponses = null;
        if (!this.previewMode && this.loadDraftFromLocalStorage()) {
            console.log('Loaded draft from localStorage');
        }

        this.beforeUnloadHandler = null;
    }

    /**
     * Render the complete wizard questionnaire interface
     */
    render() {
        this.container.innerHTML = '';

        // Create all question renderers FIRST (so display rules can access responses)
        this.questions.forEach((question) => {
            const rendererContainer = document.createElement('div');
            rendererContainer.id = `question-${question.id}`;
            const renderer = new QuestionRenderer(rendererContainer);
            // Set orchestrator reference for cross-question filtering (e.g., filterOptionsFromQuestion)
            renderer.setOrchestrator(this);
            const existingResponse = this.draftResponses?.get(question.id) || null;
            renderer.render(question, existingResponse);
            renderer.onChange = () => this.handleQuestionChange(question.id);
            renderer.onAutoAdvance = () => this.handleAutoAdvance(question.id);
            this.questionRenderers.set(question.id, renderer);
        });

        // NOW update visible questions (after renderers have responses)
        this.updateVisibleQuestions();

        // Main wizard container (600px max-height)
        const wizardContainer = document.createElement('div');
        wizardContainer.className = 'questionnaire-wizard';

        // Permanent question-number pill — hidden on the review (confirmation) panel.
        // In `pillInHeader` mode the host mounts it (panel header title slot); otherwise
        // it sits above the sliding question area so it stays put while questions
        // animate in/out.
        const questionNumber = document.createElement('div');
        questionNumber.className = 'wizard-question-number';
        questionNumber.id = 'wizard-question-number';
        const questionNumberPill = document.createElement('span');
        questionNumberPill.className = 'wizard-question-number__pill';
        questionNumberPill.id = 'wizard-question-number-pill';
        questionNumber.appendChild(questionNumberPill);
        this.questionNumberEl = questionNumber;
        if (!this.pillInHeader) {
            wizardContainer.appendChild(questionNumber);
        }

        // Question area (scrollable body — single question display)
        const questionArea = this.renderQuestionArea();
        wizardContainer.appendChild(questionArea);

        // Footer: label, progress bar, Back + Next buttons
        const footer = this.renderWizardFooter();
        wizardContainer.appendChild(footer);

        this.container.appendChild(wizardContainer);

        // Setup beforeunload handler (saves draft + warns user)
        this.setupAbandonWarning();

        // Note: showCurrentQuestion() will be called after container is added to DOM
        // See initialize() method
    }

    /**
     * Initialize the wizard after it's been added to the DOM
     * Call this after render() and after the container is in the document
     */
    initialize() {
        // Re-evaluate visible questions now that we're fully initialized
        // (in case draft responses triggered display rules)
        this.updateVisibleQuestions();

        // If we have draft responses, position at first incomplete group
        if (this.draftResponses && this.draftResponses.size > 0) {
            this.currentGroupIndex = this.findFirstIncompleteGroupIndex();

            // Emit initial progress event for restored draft responses
            // This ensures header shows correct progress when resuming a partially-completed questionnaire
            if (this.eventBus && this.pathMetadata?.id) {
                this.eventBus.emit('questionnaire:question-answered', {
                    pathId: this.pathMetadata.id,
                    pathMetadata: this.pathMetadata,
                    questionnaireId: this.questionnaire.id,
                    answeredCount: this.getAnsweredQuestionCount()
                });
            }
        }

        this.showCurrentQuestion();

        // Emit initial navigation position for dev overlay
        if (this.onNavigate) {
            this.onNavigate(this.currentGroupIndex, this.questionGroups[this.currentGroupIndex] || []);
        }
    }

    /**
     * Find the index of the first group with unanswered required questions
     * Used for draft recovery to position user at next group to complete
     * @returns {number} Index of first incomplete group, or last group if all complete
     */
    findFirstIncompleteGroupIndex() {
        for (let groupIndex = 0; groupIndex < this.questionGroups.length; groupIndex++) {
            const group = this.questionGroups[groupIndex];

            for (const question of group) {
                // Skip instruction cards (always considered "answered")
                if (question.questionType === 'INSTRUCTION_CARD') {
                    continue;
                }

                // Skip optional questions (default to required if not specified)
                if (question.isRequired === false) {
                    continue;
                }

                const renderer = this.questionRenderers.get(question.id);
                if (!renderer) continue;

                // Check if question has been answered
                const response = renderer.getResponse();
                if (!response) {
                    return groupIndex; // Found first group with unanswered required question
                }
            }
        }

        // All groups complete, return last group
        return Math.max(0, this.questionGroups.length - 1);
    }

    /**
     * Render wizard footer: linear progress bar, Back + Next buttons.
     * The "Question X of N" label lives in the top pill (`.wizard-question-number`),
     * not in the footer.
     */
    renderWizardFooter() {
        const footer = document.createElement('div');
        footer.className = 'wizard-footer';
        footer.id = 'wizard-footer';

        // Linear progress bar
        const progress = document.createElement('div');
        progress.className = 'wizard-footer__progress';
        const fill = document.createElement('div');
        fill.className = 'wizard-footer__progress-fill';
        fill.id = 'wizard-footer-progress-fill';
        progress.appendChild(fill);
        footer.appendChild(progress);

        // Buttons row (Back left, Next right) — kept under .wizard-button-container
        // so the existing slide-transition fade class (--faded) still applies.
        const buttonContainer = document.createElement('div');
        buttonContainer.className = 'wizard-button-container';
        buttonContainer.id = 'wizard-button-container';

        const backButton = document.createElement('button');
        backButton.type = 'button';
        backButton.className = 'wizard-nav-button wizard-nav-button--back';
        backButton.id = 'wizard-back-button';
        backButton.textContent = 'Back';
        backButton.setAttribute('aria-label', 'Go to previous question');
        backButton.addEventListener('click', () => this.goToPrevious());

        const nextButton = document.createElement('button');
        nextButton.type = 'button';
        nextButton.className = 'wizard-nav-button';
        nextButton.id = 'wizard-nav-button';
        nextButton.textContent = 'Next';
        nextButton.addEventListener('click', () => this.handleNextClick());

        buttonContainer.appendChild(backButton);
        buttonContainer.appendChild(nextButton);
        footer.appendChild(buttonContainer);

        return footer;
    }

    /**
     * Update footer Back button visibility.
     * Back is shown when not on the first group, or when displaying confirmation.
     */
    updateFooterButtons() {
        const backBtn = document.getElementById('wizard-back-button');
        if (!backBtn) return;

        const canGoBack = this.currentGroupIndex > 0 || this.showingConfirmation;
        backBtn.style.visibility = canGoBack ? 'visible' : 'hidden';
    }

    /**
     * Update the top question-number pill and the footer progress fill.
     * The pill is hidden on the confirmation (review) panel; the progress bar fills to 100%.
     */
    updateFooterProgress() {
        const pillWrap = document.getElementById('wizard-question-number');
        const pill = document.getElementById('wizard-question-number-pill');
        const fill = document.getElementById('wizard-footer-progress-fill');
        if (!fill) return;

        const totalGroups = this.questionGroups.length;
        if (totalGroups === 0) {
            if (pill) pill.textContent = '';
            if (pillWrap) pillWrap.classList.add('wizard-question-number--hidden');
            fill.style.width = '0%';
            return;
        }

        if (this.showingConfirmation) {
            if (pillWrap) pillWrap.classList.add('wizard-question-number--hidden');
            fill.style.width = '100%';
            return;
        }

        // Instruction cards are excluded from question numbering: the total counts
        // only answerable groups, and the current number is the position among them.
        const currentGroup = this.questionGroups[this.currentGroupIndex] || [];
        const totalAnswerable = this.questionGroups.filter(g => this.isAnswerableGroup(g)).length;
        const answerablePosition = this.questionGroups
            .slice(0, this.currentGroupIndex + 1)
            .filter(g => this.isAnswerableGroup(g)).length;

        if (this.isAnswerableGroup(currentGroup)) {
            if (pill) pill.textContent = `Question ${answerablePosition} of ${totalAnswerable}`;
        } else {
            // Instruction/explanation card: show its label instead of a number.
            const label = currentGroup[0]?.configuration?.pillLabel || DEFAULT_INSTRUCTION_PILL_LABEL;
            if (pill) pill.textContent = label;
        }

        if (pillWrap) pillWrap.classList.remove('wizard-question-number--hidden');
        // Guard against division by zero when a questionnaire has no answerable groups.
        fill.style.width = totalAnswerable > 0
            ? `${(answerablePosition / totalAnswerable) * 100}%`
            : '0%';
    }

    /**
     * Whether a group contains at least one answerable question (i.e. is not made up
     * solely of instruction/explanation cards). Used to exclude instruction cards from
     * question numbering and progress.
     * @param {Array} group - Array of questions in the group
     * @returns {boolean} True if the group has at least one non-instruction question
     */
    isAnswerableGroup(group) {
        return group.some(q => q.questionType !== 'INSTRUCTION_CARD');
    }

    /**
     * Render question area: scrollable container for the current question(s).
     * Navigation buttons live in the wizard footer, not here.
     */
    renderQuestionArea() {
        const area = document.createElement('div');
        area.className = 'wizard-question-area';
        area.id = 'wizard-question-area';

        // Question container (for slide animations)
        const questionContainer = document.createElement('div');
        questionContainer.className = 'wizard-question-container';
        questionContainer.id = 'wizard-question-container';

        area.appendChild(questionContainer);
        return area;
    }

    /**
     * Show current question group in the wizard
     * Renders all questions in the current group together
     */
    showCurrentQuestion() {
        const group = this.questionGroups[this.currentGroupIndex];
        if (!group || group.length === 0) return;

        const container = document.getElementById('wizard-question-container');
        const nextButton = document.getElementById('wizard-nav-button');

        if (!container || !nextButton) return;

        // Update Next button text - use custom label from first question in group if provided
        const primaryQuestion = group[0];
        nextButton.textContent = primaryQuestion?.configuration?.nextButtonLabel || 'Next';

        // Determine Next button visibility (Back is independent, controlled by updateFooterButtons)
        const shouldAutoAdvance = this.shouldGroupAutoAdvance(group);
        const isHybridSingleText = group.length === 1 && group[0].questionType === 'HYBRID_SINGLE_TEXT';
        const hasRequiresTextOptions = isHybridSingleText &&
            group[0].configuration?.options?.some(o => o.requiresText);

        if (shouldAutoAdvance) {
            // Auto-advance types: hide Next on first visit, show if revisiting an answered question
            const groupAnswered = group.every(q => {
                const r = this.questionRenderers.get(q.id);
                return r && r.getResponse();
            });
            nextButton.style.display = groupAnswered ? '' : 'none';
        } else if (isHybridSingleText && hasRequiresTextOptions) {
            // Hybrid single text: show Next when a requiresText option is selected,
            // or when revisiting an already-answered question; otherwise hide and auto-advance
            const question = group[0];
            const renderer = this.questionRenderers.get(question.id);
            const selectedValue = renderer?.currentResponse?.structuredData?.value;
            const selectedOpt = question.configuration?.options?.find(o => o.value === selectedValue);
            const alreadyAnswered = renderer?.getResponse() != null;
            nextButton.style.display = (selectedOpt?.requiresText || alreadyAnswered) ? '' : 'none';

            renderer.onHybridSelectionChanged = (requiresText) => {
                if (requiresText) {
                    nextButton.style.display = '';
                } else {
                    nextButton.style.display = 'none';
                    if (!this.isAnimating) {
                        setTimeout(() => {
                            const isLastGroup = this.currentGroupIndex === this.questionGroups.length - 1;
                            if (isLastGroup) {
                                this.showConfirmationPanel();
                            } else {
                                this.goToNext();
                            }
                        }, 300);
                    }
                }
            };
        } else {
            nextButton.style.display = '';
        }

        // Update footer Back button visibility
        this.updateFooterButtons();

        // Clear existing content
        container.innerHTML = '';

        // Check if this is a multi-question group
        const isMultiQuestionGroup = group.length > 1;

        if (isMultiQuestionGroup) {
            // Create group wrapper for multiple questions
            const groupWrapper = document.createElement('div');
            groupWrapper.className = 'question-group';

            // Optional: Group title (from first question in group)
            const groupTitle = group[0].groupTitle;
            if (groupTitle) {
                const titleEl = document.createElement('h3');
                titleEl.className = 'question-group-title';
                titleEl.textContent = groupTitle;
                groupWrapper.appendChild(titleEl);
            }

            // Render each question in the group
            // The renderer.container already has .discovery__question styling
            for (const question of group) {
                const renderer = this.questionRenderers.get(question.id);
                if (renderer && renderer.container) {
                    renderer.container.classList.add('question-group-item');
                    groupWrapper.appendChild(renderer.container);
                }
            }

            container.appendChild(groupWrapper);
        } else {
            // Single question - render directly (no wrapper needed)
            const question = group[0];
            const renderer = this.questionRenderers.get(question.id);
            if (renderer && renderer.container) {
                container.appendChild(renderer.container);
            }
        }

        // Update progress label + fill
        this.updateFooterProgress();

        // Scroll to top of question area
        container.scrollTop = 0;
    }

    /**
     * Update visible questions based on display rules
     */
    updateVisibleQuestions() {
        this.visibleQuestions = this.questions.filter(q => {
            // All question types (including INSTRUCTION_CARD) respect display rules
            return this.evaluateDisplayRules(q.id);
        });

        // Recompute question groups
        this.computeQuestionGroups();

        // Ensure current group index is still valid
        if (this.currentGroupIndex >= this.questionGroups.length) {
            this.currentGroupIndex = Math.max(0, this.questionGroups.length - 1);
        }

    }

    /**
     * Compute question groups from visible questions
     * Questions with the same non-null displayGroup value (consecutive) form a group
     * Questions with null displayGroup form single-question groups
     */
    computeQuestionGroups() {
        const visible = this.visibleQuestions;

        if (visible.length === 0) {
            this.questionGroups = [];
            return;
        }

        const groups = [];
        let currentGroup = [visible[0]];
        let currentDisplayGroup = visible[0].displayGroup;

        for (let i = 1; i < visible.length; i++) {
            const question = visible[i];
            const questionDisplayGroup = question.displayGroup;

            // Same non-null displayGroup = same group
            // Different displayGroup OR either is null = new group
            const sameGroup = (
                currentDisplayGroup !== null &&
                currentDisplayGroup !== undefined &&
                questionDisplayGroup !== null &&
                questionDisplayGroup !== undefined &&
                currentDisplayGroup === questionDisplayGroup
            );

            if (sameGroup) {
                currentGroup.push(question);
            } else {
                groups.push(currentGroup);
                currentGroup = [question];
                currentDisplayGroup = questionDisplayGroup;
            }
        }

        // Don't forget the last group
        if (currentGroup.length > 0) {
            groups.push(currentGroup);
        }

        this.questionGroups = groups;
    }

    /**
     * Navigate to a specific group by index with animation
     * @param {number} targetGroupIndex - Target group index
     */
    async navigateToGroup(targetGroupIndex) {
        if (this.isAnimating) return;
        if (targetGroupIndex < 0 || targetGroupIndex >= this.questionGroups.length) return;
        if (targetGroupIndex === this.currentGroupIndex) return;

        // Clear any messages from current group's questions before navigating
        const currentGroup = this.questionGroups[this.currentGroupIndex];
        if (currentGroup) {
            for (const question of currentGroup) {
                const renderer = this.questionRenderers.get(question.id);
                if (renderer) {
                    renderer.clearMessage();
                }
            }
        }

        this.isAnimating = true;

        const container = document.getElementById('wizard-question-container');
        const buttonContainer = document.getElementById('wizard-button-container');
        const slideDirection = targetGroupIndex > this.currentGroupIndex ? 'left' : 'right';

        // Fade out button and content together
        if (buttonContainer) {
            buttonContainer.classList.add('wizard-button-container--faded');
        }
        container.classList.add(`slide-out-${slideDirection}`);
        await new Promise(resolve => setTimeout(resolve, 250));

        // Update group index
        this.currentGroupIndex = targetGroupIndex;

        // Remove fade-out class and render new content
        container.classList.remove(`slide-out-${slideDirection}`);
        this.showCurrentQuestion();

        // Notify dev overlay of navigation
        if (this.onNavigate) {
            this.onNavigate(this.currentGroupIndex, this.questionGroups[this.currentGroupIndex] || []);
        }

        // Fade in button and content together
        if (buttonContainer) {
            buttonContainer.classList.remove('wizard-button-container--faded');
        }
        const slideInDirection = slideDirection === 'left' ? 'right' : 'left';
        container.classList.add(`slide-in-${slideInDirection}`);
        await new Promise(resolve => setTimeout(resolve, 300));
        container.classList.remove(`slide-in-${slideInDirection}`);

        this.isAnimating = false;
    }

    /**
     * Go to next group (with validation of all questions in current group)
     */
    async goToNext() {
        if (this.isAnimating) return;

        const currentGroup = this.questionGroups[this.currentGroupIndex];
        if (!currentGroup) return;

        // Validate ALL questions in current group (skip in preview mode)
        if (!this.previewMode) {
            let allValid = true;
            let firstInvalidRenderer = null;

            for (const question of currentGroup) {
                // Skip validation for instruction cards
                if (question.questionType === 'INSTRUCTION_CARD') continue;

                const renderer = this.questionRenderers.get(question.id);
                if (!renderer) continue;

                const isValid = renderer.isValid();

                if (!isValid && question.isRequired) {
                    if (!firstInvalidRenderer) {
                        firstInvalidRenderer = renderer;
                    }
                    renderer.showMessage('Please answer this required question', 'error');
                    allValid = false;
                }
            }

            if (!allValid) {
                return; // Block navigation
            }
        }

        // Save after answering
        this.saveDraftToLocalStorage();

        // Navigate to next group
        if (this.currentGroupIndex < this.questionGroups.length - 1) {
            const nextIndex = this.currentGroupIndex + 1;
            await this.navigateToGroup(nextIndex);
        }
    }

    /**
     * Go to previous group
     */
    async goToPrevious() {
        if (this.isAnimating) return;

        if (this.showingConfirmation) {
            // Navigate back from confirmation to last question group
            await this.navigateBackFromConfirmation();
        } else if (this.currentGroupIndex > 0) {
            await this.navigateToGroup(this.currentGroupIndex - 1);
        }
    }

    /**
     * Handle Next/Submit button click
     */
    async handleNextClick() {
        if (this.showingConfirmation) {
            // On confirmation panel - perform actual submission
            await this.performSubmission();
            return;
        }

        const isLastGroup = this.currentGroupIndex === this.questionGroups.length - 1;

        if (isLastGroup) {
            // Validate and show confirmation panel (skip in preview mode)
            if (!this.previewMode) {
                await this.showConfirmationPanel();
            }
        } else {
            // Go to next group
            await this.goToNext();
        }
    }

    /**
     * Show the confirmation panel before final submission
     * Validates all questions first, navigates to first invalid group if any fail
     */
    async showConfirmationPanel() {
        if (this.isAnimating) return;

        // Validate current group first
        const currentGroup = this.questionGroups[this.currentGroupIndex];
        let currentGroupValid = true;

        for (const question of currentGroup) {
            if (question.questionType === 'INSTRUCTION_CARD') continue;
            const renderer = this.questionRenderers.get(question.id);
            if (!renderer) continue;

            if (!renderer.isValid() && question.isRequired) {
                renderer.showMessage('Please answer this required question', 'error');
                currentGroupValid = false;
            }
        }

        if (!currentGroupValid) return;

        // Safety check: validate ALL questions (handles display rule edge cases)
        const firstInvalidGroup = this.findFirstInvalidGroup();
        if (firstInvalidGroup !== -1) {
            await this.navigateToGroup(firstInvalidGroup);
            this.showGroupValidationErrors(firstInvalidGroup);
            return;
        }

        // All valid - save draft and navigate to confirmation
        this.saveDraftToLocalStorage();
        await this.navigateToConfirmation();
    }

    /**
     * Navigate to the confirmation panel with slide animation
     */
    async navigateToConfirmation() {
        if (this.isAnimating) return;

        this.isAnimating = true;

        const container = document.getElementById('wizard-question-container');
        const buttonContainer = document.getElementById('wizard-button-container');

        // Fade out current content
        if (buttonContainer) {
            buttonContainer.classList.add('wizard-button-container--faded');
        }
        container.classList.add('slide-out-left');
        await new Promise(resolve => setTimeout(resolve, 250));

        // Set confirmation state
        this.showingConfirmation = true;

        // Remove slide-out and render confirmation
        container.classList.remove('slide-out-left');
        this.renderConfirmationPanel();

        // Notify dev overlay of confirmation navigation
        if (this.onNavigate) {
            this.onNavigate('confirmation', []);
        }

        // Update progress fill + Back button visibility for confirmation state
        this.updateFooterProgress();
        this.updateFooterButtons();

        // Fade in confirmation
        if (buttonContainer) {
            buttonContainer.classList.remove('wizard-button-container--faded');
        }
        container.classList.add('slide-in-right');
        await new Promise(resolve => setTimeout(resolve, 300));
        container.classList.remove('slide-in-right');

        this.isAnimating = false;
    }

    /**
     * Render the confirmation panel UI.
     * Re-uses the existing footer Back + Next buttons (Next text becomes "Confirm & Submit").
     */
    renderConfirmationPanel() {
        const container = document.getElementById('wizard-question-container');
        const nextBtn = document.getElementById('wizard-nav-button');

        // Clear container
        container.innerHTML = '';

        // Create confirmation content
        const confirmationPanel = document.createElement('div');
        confirmationPanel.className = 'wizard-confirmation';

        // Title
        const title = document.createElement('h3');
        title.className = 'wizard-confirmation__title';
        title.textContent = 'Ready to Submit?';

        // Summary
        const summary = document.createElement('p');
        summary.className = 'wizard-confirmation__summary';
        const answeredCount = this.getAnsweredQuestionCount();
        const totalCount = this.getTotalAnswerableQuestions();
        summary.textContent = `${answeredCount} of ${totalCount} questions answered`;

        // Warning message
        const warning = document.createElement('p');
        warning.className = 'wizard-confirmation__warning';
        warning.textContent = 'All done! You can use the back button to revisit any question before submitting.';

        // Assemble panel content
        confirmationPanel.appendChild(title);
        confirmationPanel.appendChild(summary);
        confirmationPanel.appendChild(warning);
        container.appendChild(confirmationPanel);

        // Reuse existing Next button, just change its label
        if (nextBtn) {
            nextBtn.textContent = 'Confirm & Submit';
            nextBtn.style.display = '';
            nextBtn.disabled = false;
        }
    }

    /**
     * Navigate back from confirmation panel to last question group
     */
    async navigateBackFromConfirmation() {
        if (this.isAnimating || !this.showingConfirmation) return;

        this.isAnimating = true;

        const container = document.getElementById('wizard-question-container');
        const buttonContainer = document.getElementById('wizard-button-container');

        // Fade out confirmation
        if (buttonContainer) {
            buttonContainer.classList.add('wizard-button-container--faded');
        }
        container.classList.add('slide-out-right');
        await new Promise(resolve => setTimeout(resolve, 250));

        // Clear confirmation state
        this.showingConfirmation = false;

        // Restore Next button label (Back button persists across confirmation)
        const nextBtn = document.getElementById('wizard-nav-button');
        if (nextBtn) {
            nextBtn.textContent = 'Next';
            nextBtn.style.display = '';
        }

        // Remove slide-out and render last question
        container.classList.remove('slide-out-right');
        this.showCurrentQuestion();

        // Fade in questions
        if (buttonContainer) {
            buttonContainer.classList.remove('wizard-button-container--faded');
        }
        container.classList.add('slide-in-left');
        await new Promise(resolve => setTimeout(resolve, 300));
        container.classList.remove('slide-in-left');

        this.isAnimating = false;
    }

    /**
     * Render spinner content in the confirmation panel
     */
    renderSpinnerContent() {
        const container = document.getElementById('wizard-question-container');
        if (!container) return;

        container.innerHTML = '';
        const spinnerPanel = document.createElement('div');
        spinnerPanel.className = 'wizard-confirmation--spinner';

        const spinner = document.createElement('div');
        spinner.className = 'wizard-confirmation__spinner';

        spinnerPanel.appendChild(spinner);
        container.appendChild(spinnerPanel);
    }

    /**
     * Render error content in the confirmation panel
     * @param {string} message - Error message to display
     */
    renderErrorContent(message = 'Something went wrong. Please try again.') {
        const container = document.getElementById('wizard-question-container');
        if (!container) return;

        container.innerHTML = '';
        const errorPanel = document.createElement('div');
        errorPanel.className = 'wizard-confirmation--error';

        // Error icon (X in circle)
        const iconContainer = document.createElement('div');
        iconContainer.className = 'wizard-confirmation__error-icon';
        iconContainer.innerHTML = `
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
        `;

        // Title
        const title = document.createElement('h3');
        title.className = 'wizard-confirmation__error-title';
        title.textContent = 'Submission Failed';

        // Message
        const messageEl = document.createElement('p');
        messageEl.className = 'wizard-confirmation__error-message';
        messageEl.textContent = message;

        errorPanel.appendChild(iconContainer);
        errorPanel.appendChild(title);
        errorPanel.appendChild(messageEl);
        container.appendChild(errorPanel);
    }

    /**
     * Render success content in the confirmation panel
     */
    renderSuccessContent() {
        const container = document.getElementById('wizard-question-container');
        if (!container) return;

        container.innerHTML = '';
        const successPanel = document.createElement('div');
        successPanel.className = 'wizard-confirmation--success';

        // Success icon (checkmark)
        const iconContainer = document.createElement('div');
        iconContainer.className = 'wizard-confirmation__success-icon';
        iconContainer.innerHTML = `
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
        `;

        // Title
        const title = document.createElement('h3');
        title.className = 'wizard-confirmation__success-title';
        title.textContent = 'Submitted!';

        successPanel.appendChild(iconContainer);
        successPanel.appendChild(title);
        container.appendChild(successPanel);
    }

    /**
     * Handle response change for a question
     */
    handleQuestionChange(questionId) {
        // Save response immediately
        this.saveDraftToLocalStorage();

        // Re-render any questions that filter options based on this question's response
        for (const question of this.questions) {
            if (question.configuration?.filterOptionsFromQuestion === questionId) {
                const renderer = this.questionRenderers.get(question.id);
                if (renderer) {
                    renderer.render(question, null);
                }
            }
        }

        // Re-evaluate display rules (may change visible questions / group count)
        this.updateVisibleQuestions();
        this.updateFooterProgress();

        // Emit event for optimistic header progress update
        if (this.eventBus && this.pathMetadata?.id) {
            this.eventBus.emit('questionnaire:question-answered', {
                pathId: this.pathMetadata.id,
                pathMetadata: this.pathMetadata,
                questionnaireId: this.questionnaire.id,
                answeredCount: this.getAnsweredQuestionCount()
            });
        }
    }

    /**
     * Handle auto-advance when an auto-advance question type is answered
     * @param {string} questionId - ID of the question that triggered auto-advance
     */
    handleAutoAdvance(questionId) {
        // Don't auto-advance if animating
        if (this.isAnimating) return;

        // Find which group this question belongs to
        const currentGroup = this.questionGroups[this.currentGroupIndex];
        if (!currentGroup) return;

        // Only auto-advance for single-question groups that support auto-advance
        if (!this.shouldGroupAutoAdvance(currentGroup)) return;

        // Check that the question is in the current group
        const questionInGroup = currentGroup.find(q => q.id === questionId);
        if (!questionInGroup) return;

        // Small delay for visual feedback before advancing
        setTimeout(() => {
            const isLastGroup = this.currentGroupIndex === this.questionGroups.length - 1;
            if (isLastGroup) {
                // Navigate to confirmation panel instead of blocking
                this.showConfirmationPanel();
            } else {
                // Navigate to next group
                this.goToNext();
            }
        }, 300);
    }

    /**
     * Check if a question group should auto-advance
     * A group auto-advances only if it's a single question of an auto-advance type
     * @param {Array} group - Array of questions in the group
     * @returns {boolean} True if group should auto-advance
     */
    shouldGroupAutoAdvance(group) {
        // Multi-question groups never auto-advance
        if (group.length !== 1) return false;

        const question = group[0];
        return AUTO_ADVANCE_TYPES.has(question.questionType);
    }

    /**
     * Get count of answered questions (questions with valid responses)
     * @returns {number} Count of answered questions
     */
    getAnsweredQuestionCount() {
        let count = 0;
        for (const question of this.visibleQuestions) {
            if (question.questionType === 'INSTRUCTION_CARD') continue;
            const renderer = this.questionRenderers.get(question.id);
            if (renderer?.isValid()) {
                count++;
            }
        }
        return count;
    }

    /**
     * Get total count of answerable questions (excludes INSTRUCTION_CARDs)
     * @returns {number} Count of answerable questions
     */
    getTotalAnswerableQuestions() {
        let count = 0;
        for (const question of this.visibleQuestions) {
            if (question.questionType !== 'INSTRUCTION_CARD') {
                count++;
            }
        }
        return count;
    }

    /**
     * Find the first question group with unanswered required questions
     * @returns {number} Group index, or -1 if all valid
     */
    findFirstInvalidGroup() {
        for (let i = 0; i < this.questionGroups.length; i++) {
            const group = this.questionGroups[i];
            for (const question of group) {
                if (question.questionType === 'INSTRUCTION_CARD') continue;
                const renderer = this.questionRenderers.get(question.id);
                if (!renderer) continue;

                if (!renderer.isValid() && question.isRequired) {
                    return i;
                }
            }
        }
        return -1; // All valid
    }

    /**
     * Show validation error messages for all invalid questions in a group
     * @param {number} groupIndex - Index of the group to show errors for
     */
    showGroupValidationErrors(groupIndex) {
        const group = this.questionGroups[groupIndex];
        for (const question of group) {
            if (question.questionType === 'INSTRUCTION_CARD') continue;
            const renderer = this.questionRenderers.get(question.id);
            if (!renderer) continue;

            if (!renderer.isValid() && question.isRequired) {
                renderer.showMessage('Please answer this required question', 'error');
            }
        }
    }

    /**
     * Get response for a question from its renderer
     */
    getResponseForQuestion(questionId) {
        const renderer = this.questionRenderers.get(questionId);
        return renderer ? renderer.getResponse() : null;
    }

    /**
     * Get all responses from all renderers
     */
    getAllResponses() {
        const responses = new Map();

        this.questionRenderers.forEach((renderer, questionId) => {
            // Skip INSTRUCTION_CARDs - they auto-acknowledge but aren't real responses
            const question = this.questions.find(q => q.id === questionId);
            if (question?.questionType === 'INSTRUCTION_CARD') {
                return;
            }

            // QuestionRenderer.getResponse() returns null for unanswered questions
            const response = renderer.getResponse();
            if (response !== null) {
                responses.set(questionId, response);
            }
        });

        return responses;
    }

    /**
     * Get list of question IDs that were deactivated by frontend display rules
     * These questions should be excluded from backend validation
     *
     * @returns {Array<string>} Array of deactivated question IDs
     */
    getDeactivatedQuestionIds() {
        const deactivatedIds = [];

        this.questions.forEach(question => {
            // Skip instruction cards (they don't require responses)
            if (question.questionType === 'INSTRUCTION_CARD') {
                return;
            }

            // Check if question is currently visible based on display rules
            const isVisible = this.evaluateDisplayRules(question.id);

            if (!isVisible) {
                deactivatedIds.push(question.id);
            }
        });

        return deactivatedIds;
    }

    /**
     * Evaluate if a question should be visible based on display rules
     * Implements frontend rule evaluation for UX (hybrid approach)
     *
     * Strategy:
     * 1. Start with backend isActive state (default true)
     * 2. Evaluate display rules which can override the backend state
     * 3. Deactivation rules take precedence over activation rules
     */
    evaluateDisplayRules(questionId) {
        const question = this.questions.find(q => q.id === questionId);

        // Start with backend state (default true if not specified)
        let isActive = question?.isActive ?? true;

        const rules = this.displayRules.filter(rule => rule.targetQuestionId === questionId);

        if (rules.length === 0) {
            return isActive; // No display rules, use backend state
        }

        // Evaluate display rules (can override backend state)
        let shouldActivate = false;
        let shouldDeactivate = false;

        for (const rule of rules) {
            const sourceRenderer = this.questionRenderers.get(rule.sourceQuestionId);
            if (!sourceRenderer) continue;

            const response = sourceRenderer.getResponse();
            if (!response) continue;

            // Get value from response
            const value = response.structuredData?.value || response.textData;

            // Evaluate condition
            const conditionMet = this.evaluateCondition(rule.condition, value);

            // Track which actions would be triggered
            if (conditionMet) {
                if (rule.action === 'activate') {
                    shouldActivate = true;
                } else if (rule.action === 'deactivate') {
                    shouldDeactivate = true;
                }
            }
        }

        // Apply display rule overrides
        if (shouldDeactivate) return false; // Deactivation takes precedence
        if (shouldActivate) return true;

        // No rules triggered, return backend state
        return isActive;
    }

    /**
     * Evaluate a single condition
     */
    evaluateCondition(condition, value) {
        const { operator, value: compareValue } = condition;

        switch (operator) {
            case 'equals':
                return value === compareValue;
            case 'not_equals':
                return value !== compareValue;
            case 'in':
                // compareValue is an array of allowed values
                return Array.isArray(compareValue) && compareValue.includes(value);
            case 'not_in':
                // compareValue is an array of disallowed values
                return Array.isArray(compareValue) && !compareValue.includes(value);
            case 'greater_than':
                return parseFloat(value) > parseFloat(compareValue);
            case 'less_than':
                return parseFloat(value) < parseFloat(compareValue);
            case 'greater_than_or_equals':
                return parseFloat(value) >= parseFloat(compareValue);
            case 'less_than_or_equals':
                return parseFloat(value) <= parseFloat(compareValue);
            case 'contains':
                return String(value).toLowerCase().includes(String(compareValue).toLowerCase());
            default:
                return false;
        }
    }

    /**
     * Validate all visible required questions
     * @param {boolean} showErrors - Whether to display validation errors
     * @returns {boolean} Whether all validations pass
     */
    validateAll(showErrors = true) {
        let allValid = true;

        this.visibleQuestions.forEach(question => {
            if (question.questionType === 'INSTRUCTION_CARD') return;

            const renderer = this.questionRenderers.get(question.id);
            if (!renderer) return;

            const isValid = renderer.isValid();

            if (!isValid && question.isRequired) {
                allValid = false;
                if (showErrors) {
                    renderer.showMessage('This question is required', 'error');
                }
            }
        });

        return allValid;
    }

    /**
     * Perform the actual submission (called from confirmation panel)
     * Validation has already been done before showing confirmation
     */
    async performSubmission() {
        // Mark as submitted to prevent abandon warning
        this.submitted = true;

        // Get button reference and disable during submission
        const submitBtn = document.getElementById('wizard-nav-button');
        if (submitBtn) submitBtn.disabled = true;

        // Show spinner content (replaces confirmation content)
        this.renderSpinnerContent();

        try {
            // Get all responses from renderers (Map: questionId -> responseData)
            const responses = this.getAllResponses();

            // Get deactivated question IDs (questions hidden by display rules)
            const deactivatedQuestionIds = this.getDeactivatedQuestionIds();

            if (this.previewMode && this.onPreviewSubmit) {
                // Dev preview path: skip persistence, hand a flattened array to the callback.
                // Callback MUST NOT throw — it is expected to surface errors via its own UI.
                const flat = Array.from(responses.entries()).map(([questionId, data]) => ({
                    questionId,
                    structuredData: data.structuredData ?? null,
                    textData: data.textData ?? null
                }));
                await this.onPreviewSubmit(flat);
            } else if (this.onSubmit) {
                // Call onSubmit callback with responses Map and deactivated IDs
                // (API layer handles Map -> wire format conversion)
                await this.onSubmit(responses, deactivatedQuestionIds);
            }

            // Clear draft (completed submission)
            this.clearDraft();

            // Show success content
            this.renderSuccessContent();

            // Wait briefly to show success, then complete
            await new Promise(resolve => setTimeout(resolve, 500));

            // Call onComplete callback (triggers launcher to close panel and update state)
            if (this.onComplete) {
                await this.onComplete({
                    questionnaire: this.questionnaire,
                    pathMetadata: this.pathMetadata,
                    responses,
                    state: 'completed'
                });
            }

        } catch (error) {
            console.error('Submission failed:', error);

            // discoveryApi._request throws a plain object whose human message
            // lives on `.error` (not `.message`). Surface the backend's
            // validation detail; keep the generic copy for everything else.
            const message = error?.code === 'INCOMPLETE_SUBMISSION' && error?.error
                ? error.error
                : 'Submission failed. Please try again.';
            this.renderErrorContent(message);

            // Re-enable submit button with "Try Again" text
            if (submitBtn) {
                submitBtn.disabled = false;
                submitBtn.textContent = 'Try Again';
            }

            this.submitted = false; // Re-enable abandon warning
        }
    }

    /**
     * Save draft to localStorage
     * Includes pathId for resume detection when loading by path.
     */
    saveDraftToLocalStorage() {
        if (this.previewMode) return;
        const responses = this.getAllResponses();
        if (responses.size === 0) return; // Don't save empty drafts

        const draft = {
            questionnaireId: this.questionnaire.id,
            pathId: this.pathMetadata?.id || null,
            responses: Array.from(responses.entries()),
            timestamp: Date.now()
        };

        localStorage.setItem(
            `questionnaire_draft_${this.questionnaire.id}`,
            JSON.stringify(draft)
        );
    }

    // ============================================================
    // DEV-ONLY: Questionnaire draft preservation on localhost
    // ============================================================
    //
    // WHY THIS EXISTS:
    //   During development we constantly reset Discovery and Orientation
    //   to test flows end-to-end. Without this, every reset wipes the
    //   localStorage draft on submit, forcing us to manually re-answer
    //   every questionnaire from scratch on every iteration.
    //
    //   When running on localhost, we instead preserve the draft after
    //   submission. On the next launch of the same questionnaire (after
    //   a discovery reset), `loadDraftFromLocalStorage()` rehydrates
    //   the prior answers and the user lands straight at the
    //   confirmation panel, ready to resubmit with one click.
    //
    //   We also bypass the 48-hour draft expiry in this mode so drafts
    //   survive across long dev breaks.
    //
    // SCOPE / SAFETY:
    //   Gated strictly on `window.location.hostname` being `localhost`
    //   or `127.0.0.1`. Production hostnames (e.g. app.encouple.com)
    //   will never hit this branch, so behavior in prod is identical
    //   to before this block was added.
    //
    // REMOVAL:
    //   This entire `_isDevPreserveMode` helper, the `||` branch in
    //   `loadDraftFromLocalStorage()` that bypasses the 48h expiry,
    //   and the early-return in `clearDraft()` can all be deleted
    //   without touching any production behavior. Search for
    //   `_isDevPreserveMode` to find every call site.
    // ============================================================
    _isDevPreserveMode() {
        const host = window.location.hostname;
        return host === 'localhost' || host === '127.0.0.1';
    }

    /**
     * Load draft from localStorage
     */
    loadDraftFromLocalStorage() {
        if (this.previewMode) return false;
        const key = `questionnaire_draft_${this.questionnaire.id}`;
        const draft = localStorage.getItem(key);

        if (draft) {
            try {
                const { responses, timestamp } = JSON.parse(draft);

                // Expire drafts older than 48 hours.
                // DEV-ONLY: `_isDevPreserveMode()` short-circuits the expiry
                // on localhost so preserved drafts never age out during
                // development. Remove this `||` clause to restore the
                // strict 48h TTL in all environments.
                if (this._isDevPreserveMode() || Date.now() - timestamp < 172800000) {
                    this.draftResponses = new Map(responses);
                    return true;
                } else {
                    localStorage.removeItem(key);
                }
            } catch (error) {
                console.error('Failed to load draft:', error);
            }
        }

        return false;
    }

    /**
     * Clear draft from localStorage
     */
    clearDraft() {
        // DEV-ONLY: on localhost, skip the delete so the draft survives
        // submission and pre-populates the next Discovery/Orientation
        // reset cycle. Remove this entire `if` block to restore the
        // standard production behavior (always clear on submit).
        if (this._isDevPreserveMode()) {
            console.info(
                `[dev] Preserving questionnaire draft ${this.questionnaire.id} — localhost detected`
            );
            return;
        }
        localStorage.removeItem(`questionnaire_draft_${this.questionnaire.id}`);
    }

    /**
     * Setup abandon warning
     */
    setupAbandonWarning() {
        if (this.previewMode) return;
        this.beforeUnloadHandler = (e) => {
            const responses = this.getAllResponses();
            if (responses.size > 0 && !this.submitted) {
                // Final save before page unload
                this.saveDraftToLocalStorage();
                e.preventDefault();
                e.returnValue = 'You have unsaved answers. Are you sure you want to leave?';
                return e.returnValue;
            }
        };
        window.addEventListener('beforeunload', this.beforeUnloadHandler);
    }

    /**
     * Collapse the questionnaire with animation and create collapsed button
     * @param {string} state - State of questionnaire: 'completed' or 'abandoned'
     * @returns {Promise<HTMLElement>} Resolves with collapsed button element after animation
     */
    async collapse(state = 'abandoned') {
        // Get wizard container for animation
        const wizardContainer = this.container?.querySelector('.questionnaire-wizard');
        if (!wizardContainer) {
            return this.createCollapsedButton(state);
        }

        // Calculate current height for animation
        const currentHeight = wizardContainer.offsetHeight;

        // Set explicit height for transition
        wizardContainer.style.height = `${currentHeight}px`;
        wizardContainer.style.overflow = 'hidden';

        // Force reflow to ensure height is applied before transition
        wizardContainer.offsetHeight;

        // Add collapsing class and trigger animation
        wizardContainer.style.transition = 'height 500ms ease-out, opacity 500ms ease-out';
        wizardContainer.style.height = '0px';
        wizardContainer.style.opacity = '0';

        // Wait for animation to complete
        await new Promise(resolve => setTimeout(resolve, 500));

        // Create collapsed button
        const collapsedButton = this.createCollapsedButton(state);

        // Replace wizard container with collapsed button
        if (this.container) {
            this.container.innerHTML = '';
            this.container.appendChild(collapsedButton);

            // Scroll collapsed button to top of viewport (like user messages)
            requestAnimationFrame(() => {
                const messageContainer = this.container.closest('.message--interactive');
                if (messageContainer && window.chatManager?.messageList) {
                    window.chatManager.messageList.scrollToMessage(messageContainer);
                }
            });
        }

        return collapsedButton;
    }

    /**
     * Create collapsed button element
     * @param {string} state - State of questionnaire: 'completed' or 'abandoned'
     * @returns {HTMLElement} Collapsed button element
     */
    createCollapsedButton(state = 'abandoned') {
        const button = document.createElement('button');
        button.className = `questionnaire-collapsed-btn questionnaire-collapsed-btn--${state}`;
        button.dataset.pathId = this.pathMetadata?.id || '';
        button.dataset.questionnaireId = this.questionnaire.id;
        button.dataset.pathName = this.pathMetadata?.name || '';
        button.dataset.questionnaireName = this.questionnaire.name;
        button.dataset.state = state;

        // Store full path metadata as a JS property for resume handling.
        button._pathMetadata = this.pathMetadata || null;

        const title = document.createElement('span');
        title.className = 'questionnaire-collapsed-btn__title';
        title.textContent = this.questionnaire.name;

        button.appendChild(title);

        return button;
    }

    /**
     * Cleanup and destroy orchestrator
     * @param {Object} options - Cleanup options
     * @param {boolean} options.clearDraft - Whether to clear localStorage draft (default: false, only clear on completion)
     * @param {boolean} options.removeFromDOM - Whether to remove container from DOM (default: false)
     */
    destroy(options = {}) {
        const { clearDraft = false, removeFromDOM = false } = options;

        // 1. Clear or preserve draft based on scenario
        if (clearDraft) {
            this.clearDraft();
        }
        // Note: If not clearing, draft remains in localStorage for later resumption

        // 3. Remove window event listeners
        if (this.beforeUnloadHandler) {
            window.removeEventListener('beforeunload', this.beforeUnloadHandler);
            this.beforeUnloadHandler = null;
        }

        // 4. Clear animation state
        this.isAnimating = false;

        // 5. Destroy all question renderers
        this.questionRenderers.forEach(renderer => {
            if (renderer.destroy) {
                renderer.destroy();
            }
        });
        this.questionRenderers.clear();

        // 6. Clear state
        this.visibleQuestions = [];
        this.questionGroups = [];
        this.currentGroupIndex = 0;
        this.submitted = false;

        // 7. Detach the question-number pill (in pillInHeader mode it lives outside
        // this.container, so container teardown alone would strand it)
        if (this.questionNumberEl?.parentNode) {
            this.questionNumberEl.parentNode.removeChild(this.questionNumberEl);
        }
        this.questionNumberEl = null;

        // 8. Optionally remove from DOM
        if (removeFromDOM && this.container && this.container.parentNode) {
            this.container.parentNode.removeChild(this.container);
        }

        // 9. Clear container reference
        this.container = null;
    }

    /**
     * Handle preview refresh — re-fetches questionnaire data and rebuilds from Q1.
     * Dev-only, used during questionnaire editing workflow.
     */
    async handlePreviewRefresh() {
        if (!this.onRefresh) return;

        try {
            const newData = await this.onRefresh();

            // Update internal data
            this.questionnaire = newData.questionnaire;
            this.questions = newData.questions;
            this.displayRules = newData.displayRules || [];

            // Reset state
            this.questionRenderers.clear();
            this.currentGroupIndex = 0;
            this.showingConfirmation = false;

            // Clear and re-render
            this.container.innerHTML = '';
            this.render();
            this.initialize();

            console.log('[Preview] Refreshed questionnaire data');
        } catch (error) {
            console.error('[Preview] Refresh failed:', error);
        }
    }
}
